import React from "react";
import { message } from "antd";
import "../assets/css/solanawalletgenerator.css";

type GeneratedWallet = {
    publicKeyBase58: string;
    privateKeyHex: string; // 64-byte secret key as hex string
    privateKeyBytes: number[]; // for JSON downloads if needed
};

// Minimal base58 encoder for fallback
const BASE58_ALPHABET = "123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz";
function toBase58(bytes: Uint8Array): string {
    if (!bytes || bytes.length === 0) return "";
    let zeros = 0;
    let i = 0;
    while (i < bytes.length && bytes[i] === 0) { zeros++; i++; }
    const base = BASE58_ALPHABET.length;
    const digits: number[] = [0];
    for (; i < bytes.length; i++) {
        let carry = bytes[i];
        for (let j = 0; j < digits.length; j++) {
            const x = digits[j] * 256 + carry;
            digits[j] = x % base;
            carry = Math.floor(x / base);
        }
        while (carry > 0) {
            digits.push(carry % base);
            carry = Math.floor(carry / base);
        }
    }
    let out = "";
    for (let k = 0; k < zeros; k++) out += BASE58_ALPHABET[0];
    for (let q = digits.length - 1; q >= 0; q--) out += BASE58_ALPHABET[digits[q]];
    return out;
}

function bytesToHex(bytes: Uint8Array): string {
    let hex = "";
    for (let i = 0; i < bytes.length; i++) {
        const h = bytes[i].toString(16).padStart(2, "0");
        hex += h;
    }
    return hex;
}

async function fallbackGenerateViaWebCrypto(): Promise<GeneratedWallet> {
    const keyPair = await window.crypto.subtle.generateKey(
        { name: "Ed25519", namedCurve: "Ed25519" } as EcKeyGenParams,
        true,
        ["sign", "verify"]
    );
    const publicKeyRaw = new Uint8Array(await window.crypto.subtle.exportKey("raw", keyPair.publicKey));
    const privateKeyPkcs8 = new Uint8Array(await window.crypto.subtle.exportKey("pkcs8", keyPair.privateKey));

    // Heuristic to get 32-byte seed and build 64-byte secret (seed + public)
    let seed32: Uint8Array | null = null;
    for (let size = 32; size <= 64 && !seed32; size++) {
        if (privateKeyPkcs8.length >= size) {
            const candidate = privateKeyPkcs8.slice(privateKeyPkcs8.length - size);
            if (candidate.length >= 32) seed32 = candidate.slice(candidate.length - 32);
        }
    }
    let secret: Uint8Array;
    if (seed32) {
        secret = new Uint8Array(seed32.length + publicKeyRaw.length);
        secret.set(seed32, 0);
        secret.set(publicKeyRaw, seed32.length);
    } else {
        secret = privateKeyPkcs8;
    }
    return {
        publicKeyBase58: toBase58(publicKeyRaw),
        privateKeyHex: bytesToHex(secret),
        privateKeyBytes: Array.from(secret),
    };
}

async function generateSolanaKeypair(): Promise<GeneratedWallet> {
    try {
        const web3: any = await (eval('import'))('@solana/web3.js');
        const kp = web3.Keypair.generate();
        const publicKeyBase58 = kp.publicKey.toBase58();
        const secretBytes = kp.secretKey as Uint8Array; // 64 bytes
        const privateKeyHex = bytesToHex(secretBytes);
        return {
            publicKeyBase58,
            privateKeyHex,
            privateKeyBytes: Array.from(secretBytes),
        };
    } catch (err) {
        console.error('Falling back to WebCrypto generator:', err);
        message.warning("@solana/web3.js not found. Using browser crypto fallback.");
        return await fallbackGenerateViaWebCrypto();
    }
}

const SolanaWalletGenerator: React.FC = () => {
    const [wallet, setWallet] = React.useState<GeneratedWallet | null>(null);
    const [busy, setBusy] = React.useState(false);

    const onGenerate = async () => {
        try {
            setBusy(true);
            const w = await generateSolanaKeypair();
            setWallet(w);
            // show fields below
        } catch (e) {
            message.error("Failed to generate wallet. Your browser must support Ed25519 WebCrypto.");
        } finally {
            setBusy(false);
        }
    };

    const copyToClipboard = async (value: string) => {
        try {
            await navigator.clipboard.writeText(value);
            message.success("Copied to clipboard");
        } catch {
            message.error("Copy failed");
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-[#00B4D8] via-[#48CAB2] to-[#90ED8F] flex items-center justify-center px-4">
            <div style={{ width: "100%", maxWidth: 760 }}>
                <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-xl p-8 max-w-2xl w-full space-y-8">
                    <div className="flex items-center justify-center space-x-3"><h1 className="text-3xl font-bold bg-gradient-to-r from-[#00B4D8] to-[#90ED8F] text-transparent bg-clip-text">Ailtra Solana Wallet Generator</h1></div>
                    <button style={{ marginTop: "30px" }} onClick={onGenerate} disabled={busy} className="w-full py-3 px-4 bg-gradient-to-r from-[#00B4D8] to-[#48CAB2] text-white rounded-lg
                     hover:from-[#00A3C4] hover:to-[#3BB89F] transition-all duration-300
                      flex items-center justify-center space-x-2 font-medium shadow-lg btn-generate">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-key w-5 h-5"><circle cx="7.5" cy="15.5" r="5.5"></circle><path d="m21 2-9.6 9.6"></path><path d="m15.5 7.5 3 3L22 7l-3-3"></path></svg>
                        <span>Generate New Wallet</span>
                    </button>
                    {wallet && (
                        <div className="space-y-4">
                            <div className="space-y-2">
                                <label className="block text-sm font-medium text-[#00B4D8]">Public Key</label>
                            </div>
                            <div className="flex items-center space-x-2"><code className="flex-1 p-3 bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg text-sm break-all border border-gray-100">{wallet.publicKeyBase58}</code>
                                <button className="p-2 text-gray-600 hover:text-[#48CAB2] transition-colors" title="Copy public key" onClick={() => copyToClipboard(wallet.publicKeyBase58)}>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="lucide lucide-copy w-5 h-5"><rect width="14" height="14" x="8" y="8" rx="2" ry="2"></rect><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"></path>
                                    </svg>
                                </button>
                            </div>
                            <div className="space-y-2">
                                <label className="block text-sm font-medium text-[#48CAB2]">Private Key</label>
                            </div>
                            <div className="flex items-center space-x-2"><code className="flex-1 p-3 bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg text-sm break-all border border-gray-100">{wallet.privateKeyHex}</code>
                                <button className="p-2 text-gray-600 hover:text-[#48CAB2] transition-colors" title="Copy private key" onClick={() => copyToClipboard(wallet.privateKeyHex)}>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="lucide lucide-copy w-5 h-5"><rect width="14" height="14" x="8" y="8" rx="2" ry="2"></rect><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"></path>
                                    </svg>
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SolanaWalletGenerator;